import React, {
  forwardRef,
  FunctionComponent,
  useEffect,
  useState
} from "react";
import MaterialTable from "@material-table/core";
import { Store } from "../../../../models/Store";
import { Alert } from "@material-ui/lab";
import {
  MinimalStoreInventoryItem,
  StoreInventoryItem
} from "../../../../models/StoreInventory";
import * as StoreInventoryApi from "../../../../api/storeInventoryApi";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Tooltip,
  useMediaQuery,
  Zoom
} from "@material-ui/core";
import { Product } from "../../../../models/Product";
import Typography from "@material-ui/core/Typography";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import useStyles from "./styles";
import { AddProductList } from "./AddProductList";
import { CategoryMultiSelect } from "../../../../components/selects/CategoryMultiSelect";
import { DistributorMultiSelect } from "../../../../components/selects/DistributorMultiSelect";
import { BrandNameMultiSelect } from "../../../../components/selects/BrandNameMultiSelect";
import DateRangePicker from "../../../../components/inputs/daterangepicker";
import moment from "moment";
import { LiveHelp, SaveAlt } from "@material-ui/icons";
import { ProductDetails } from "../../ProductManagement/ProductDetails";
import { useTheme } from "@material-ui/core/styles";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import { globalSettings } from "../../../../models/Settings";
import { useI18n } from "../../../../hooks/useI18n";
import { ScanditBarcodeScanner } from "../../../../components/ScanditBarcodeScanner";

interface StoreInventoryProps {
  store: Store;
}

export const StoreInventory: FunctionComponent<StoreInventoryProps> = ({
  store
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const tableRef = React.createRef();
  const { t } = useI18n();

  const defaultFilters = {
    display: false,
    dateRange: [moment().toDate(), moment().toDate()],
    storeId: store.id,
    categoryIds: [],
    distributors: [],
    brandNames: []
  };

  const [deleteInventoryItems, setDeleteInventoryItems] = useState({
    displayConfirmDelete: false,
    toBeDeleted: [] as ReadonlyArray<MinimalStoreInventoryItem>,
    status: 0
  });

  const [addInventoryItem, setAddInventoryItem] = useState({
    displayAddItem: false
  });

  const [filters, setFilters] = useState<any>(defaultFilters);

  const [reloadInventory, setReloadInventory] = useState<boolean>(true);
  const [storeInventoryItems, setStoreInventoryItems] = useState<
    StoreInventoryItem[]
  >([]);

  const [showScanner, setShowScanner] = useState<boolean>(false);

  const [productDetails, setProductDetails] = useState({
    display: false,
    gtin: ""
  });

  useEffect(() => {
    const fetchInventory = async () => {
      const result = await StoreInventoryApi.listWithStatistics(
        store.id,
        filters
      );
      if (result.type === "OK") {
        updateStoreInventoryItems(result.value as StoreInventoryItem[]);
      }
      setReloadInventory(false);
    };

    if (reloadInventory) {
      fetchInventory();
    }
  }, [reloadInventory]);

  const resetFilters = () => {
    setFilters(defaultFilters);
    setReloadInventory(true);
  };

  const applyFilters = () => {
    setFilters({ ...filters, display: false });
    setReloadInventory(true);
  };

  const setDateRange = (dateRange: any) => {
    setFilters({ ...filters, dateRange: dateRange });
  };

  const updateInventory = (updatedItem: any, add: boolean) => {
    if (add) {
      return updateStoreInventoryItems([...storeInventoryItems, updatedItem]);
    }

    const updatedData = storeInventoryItems.map(x =>
      x.productId === updatedItem.productId
        ? {
            ...x,
            price: updatedItem.price,
            articleNumber: updatedItem.articleNumber,
            orderUnit: updatedItem.orderUnit,
            maxOnShelf: updatedItem.maxOnShelf,
            orderThreshold: updatedItem.orderThreshold,
            hasPriceTag: updatedItem.hasPriceTag,
            isOrderable: updatedItem.isOrderable,
            onShelf: updatedItem.onShelf
          }
        : x
    );
    updateStoreInventoryItems(updatedData);
  };

  const updateStoreInventoryItems = (inventoryItems: any[]) => {
    setStoreInventoryItems(inventoryItems);
    setReloadInventory(true);
  };

  return (
    <>
      <MaterialTable
        title=""
        tableRef={tableRef}
        columns={[
          {
            title: "GTIN",
            field: "gtin",
            searchable: true,
            hidden: true,
            export: true
          },
          {
            title: "Product",
            field: "productName",
            cellStyle: { padding: mobile ? "8px 20px 8px 8px" : "" }
          },
          {
            title: "Art.no.",
            field: "articleNumber",
            export: false,
            cellStyle: { padding: mobile ? "8px" : "" }
          },
          {
            title: "Priority",
            field: "priority.name",
            searchable: true,
            customFilterAndSearch: (term, rowData) => {
              if (!term.startsWith("priority:")) return false;
              return term.includes(rowData.priority?.name);
            },
            hidden: true,
            export: true,
            cellStyle: { padding: mobile ? "8px" : "" },
            render: (rowData: any) =>
              rowData.priority !== undefined ? rowData.priority.name : "-"
          },
          {
            title: t("common.formFields.onShelf"),
            field: "onShelf",
            cellStyle: { padding: mobile ? "8px" : "" },
            render: (rowData: any) =>
              rowData.onShelf !== undefined ? rowData.onShelf : "-"
          },
          {
            title: "Sold",
            field: "quantitySold",
            cellStyle: { padding: mobile ? "8px" : "" },
            export: true,
            render: (rowData: any) =>
              rowData.quantitySold !== undefined ? rowData.quantitySold : "-"
          },
          {
            title: t("common.formFields.acquisitionPrice"),
            field: "acquisitionPrice",
            hidden: true,
            export: true,
            render: (rowData: any) => rowData.acquisitionPrice
          },
          {
            title: t("common.tableHeaders.shelfValue"),
            field: "onShelfWorth",
            hidden: true,
            export: true
          },
          {
            title: "Brand",
            field: "brandName",
            searchable: true,
            hidden: true
          }
        ]}
        onRowClick={(evt, row) =>
          setProductDetails({ display: true, gtin: row.gtin })
        }
        data={
          (storeInventoryItems && storeInventoryItems.length) || reloadInventory
            ? (storeInventoryItems as [])
            : []
        }
        isLoading={!storeInventoryItems || reloadInventory}
        options={{
          pageSize: 20,
          pageSizeOptions: [10, 20, 50, 100],
          searchFieldAlignment: mobile ? "left" : "right",
          headerStyle: {
            padding: mobile ? "8px" : ""
          },
          exportAllData: true,
          exportMenu: [
            {
              label: t("common.export.exportPdf"),
              exportFunc: (cols, data) =>
                ExportPdf(cols, data, `OnShelf_${store.name}`)
            },
            {
              label: t("common.export.exportCsv"),
              exportFunc: (cols, data) =>
                ExportCsv(cols, data, `OnShelf_${store.name}`)
            }
          ]
        }}
        icons={{
          Export: forwardRef((props, ref) => (
            <SaveAlt style={{ marginTop: 4 }} {...props} ref={ref} />
          ))
        }}
        actions={[
          {
            icon: "filter_list",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.addFilters"),
            isFreeAction: true,
            onClick: () => {
              return setFilters({
                ...filters,
                display: true
              });
            }
          },
          {
            icon: "tablet_android",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.scanProduct"),
            isFreeAction: true,
            onClick: () => {
              setShowScanner(true);
            }
          },
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: "Activate product in store",
            isFreeAction: true,
            onClick: () => {
              return setAddInventoryItem({
                ...addInventoryItem,
                displayAddItem: true
              });
            }
          }
        ]}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"xl"}
        open={productDetails.display}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography style={{ padding: 24 }}>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setProductDetails({
                ...productDetails,
                display: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ProductDetails
            storeId={store.id}
            usesDigitalPriceTags={store.features?.usesDigitalPriceTags}
            storeName={store.name}
            gtin={productDetails.gtin}
            onSave={updateInventory}
            onDelete={productId => {
              setStoreInventoryItems(
                storeInventoryItems.filter(item => item.productId !== productId)
              );
              setProductDetails({ ...productDetails, display: false });
              setDeleteInventoryItems({ ...deleteInventoryItems, status: 1 });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullScreen={mobile}
        fullWidth={true}
        maxWidth={"sm"}
        open={addInventoryItem.displayAddItem}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h6">Products</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setAddInventoryItem({
                ...addInventoryItem,
                displayAddItem: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          <AddProductList
            storeId={store.id}
            currentInventoryItems={storeInventoryItems}
            updateInventory={setStoreInventoryItems}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={filters.display}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setFilters({
                ...filters,
                display: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <div className={classes.dateRangeContainer}>
            <DateRangePicker
              disabled={false}
              onChange={setDateRange}
              value={filters.dateRange}
            />
            <Tooltip
              classes={{ tooltip: classes.dateRangeTooltip }}
              title={t("common.tooltip.thisPeriodUsedForOnShelfAndSold")}
              TransitionComponent={Zoom}
              leaveDelay={400}
              arrow
            >
              <LiveHelp className={classes.dateRangeHelp} />
            </Tooltip>
          </div>
          <CategoryMultiSelect
            className={classes.field}
            value={filters.categoryIds}
            onSelected={categories =>
              setFilters({
                ...filters,
                categoryIds:
                  categories.length > 0 ? categories.map(c => c.id) : []
              })
            }
          />
          <DistributorMultiSelect
            className={classes.field}
            value={filters.distributorIds}
            onSelected={distributors =>
              setFilters({
                ...filters,
                distributorIds:
                  distributors.length > 0 ? distributors.map(d => d.id) : []
              })
            }
          />
          <BrandNameMultiSelect
            className={classes.field}
            value={filters.brandNames}
            onSelected={brandNames =>
              setFilters({
                ...filters,
                brandNames:
                  brandNames.length > 0 ? brandNames.map(bn => bn) : []
              })
            }
          />
          <div className={classes.filterButtons}>
            <Button variant="contained" onClick={resetFilters}>
              Reset
            </Button>
            <Button
              className={classes.applyFilter}
              variant="contained"
              color="primary"
              onClick={applyFilters}
            >
              Apply filters
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        open={showScanner}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setShowScanner(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent
          style={
            mobile
              ? { padding: 0, marginTop: 15 }
              : { marginTop: 15, marginBottom: 30 }
          }
        >
          <div
            className={classes.scanner}
            style={mobile ? { display: "flex", height: "70%" } : {}}
          >
            {showScanner && (
              <ScanditBarcodeScanner
                licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
                libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
                onScan={(result: any) => {
                  const { barcodes } = result;
                  if (barcodes.length > 0) {
                    const barcodeData = barcodes[0].data;
                    setShowScanner(false);
                    setProductDetails({
                      ...productDetails,
                      display: true,
                      gtin: barcodeData
                    });
                  }
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={deleteInventoryItems.status > 0}
        autoHideDuration={3000}
        onClose={() => {
          setDeleteInventoryItems({
            ...deleteInventoryItems,
            displayConfirmDelete: false,
            status: 0,
            toBeDeleted: []
          });
        }}
      >
        {deleteInventoryItems.status > 0 ? (
          <Alert
            severity={deleteInventoryItems.status === 1 ? "success" : "error"}
          >
            {deleteInventoryItems.status === 1
              ? "Successfully deactivated selected products"
              : "Failed to deactivate selected products"}
          </Alert>
        ) : (
          <div />
        )}
      </Snackbar>
    </>
  );
};
